import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { apiRequest } from '@/src/services';
import { getZeroOption, type Option } from '@/src/utils';
import { SimpleButton, SimpleSingleSelect, FilterIcon, ICON_COLORS, ErrorMessage } from '@/src/ui';
import type { InitialOptions, FilterState } from '../models/filterDTOs';

interface FiltersProps {
    refreshTableData: (filters: FilterState) => void;
    setSearchString: (val: string) => void;
    filterState: FilterState;
    setFilterState: React.Dispatch<React.SetStateAction<FilterState>>;
}

const AllOption = getZeroOption('All');
const LoadingOption = getZeroOption('Loading...');

// TODO: Does not load based on selected options on refresh call
const Filters: React.FC<FiltersProps> = ({ refreshTableData, filterState, setFilterState }) => {
    const [error, setError] = useState('');

    const [examTypeMasterOptions, setExamTypeMasterOptions] = useState<Array<Option>>([
        LoadingOption,
    ]);
    const [gradeLevelMasterOptions, setGradeLevelMasterOptions] = useState<Array<Option>>([
        LoadingOption,
    ]);

    const [fieldsChanged, setFieldsChanged] = useState(false);

    useEffect(() => {
        (async function init() {
            try {
                const res = await apiRequest<InitialOptions>(
                    'GET',
                    'utility/filterData?getExamTypeMaster=true&getGradeLevelMaster=true'
                );
                const data = res.data;

                const newExamTypeMasterOptions = data.examTypeMasterOptions.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
                setExamTypeMasterOptions([AllOption, ...newExamTypeMasterOptions]);

                setFilterState((prev) => {
                    if (
                        typeof prev.examTypeMasterId === 'undefined' ||
                        prev.examTypeMasterId === null
                    ) {
                        return { ...prev, examTypeMasterId: Number(AllOption.id) };
                    }
                    return prev;
                });

                const newGradeLevelMasterOptions = data.gradeLevelMasterOptions;
                setGradeLevelMasterOptions([AllOption, ...newGradeLevelMasterOptions]);
                setFilterState((prev) => {
                    if (
                        typeof prev.gradeLevelMasterId === 'undefined' ||
                        prev.gradeLevelMasterId === null
                    ) {
                        return { ...prev, gradeLevelMasterId: Number(AllOption.id) };
                    }
                    return prev;
                });
            } catch {
                setError('Failed to fetch grade and district options.');
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                justifyContent: 'center',
                mb: 2,
            }}
        >
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 1 }}>
                <SimpleSingleSelect
                    name="examTypeMasterId"
                    label="Type"
                    value={filterState.examTypeMasterId}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        setFilterState((prev) => ({
                            ...prev,
                            examTypeMasterId: Number(e.target.value),
                        }));
                    }}
                    options={examTypeMasterOptions}
                    error={error ? { message: error } : undefined}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleSingleSelect
                    name="gradeLevelMasterId"
                    label="Grade Level"
                    value={filterState.gradeLevelMasterId}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newGradeLevel = Number(e.target.value);
                        setFilterState((prev) => ({ ...prev, gradeLevelMasterId: newGradeLevel }));
                    }}
                    options={gradeLevelMasterOptions}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <Box sx={{ justifyContent: 'center' }}>
                    <SimpleButton
                        onClick={() => {
                            setFieldsChanged(false);
                            refreshTableData(filterState);
                        }}
                        variant={fieldsChanged ? 'outlined' : 'contained'}
                        color="primary"
                    >
                        <FilterIcon
                            color={fieldsChanged ? ICON_COLORS.primary : ICON_COLORS.secondary}
                            style={{ width: '16px', height: '16px', marginRight: '8px' }}
                        />{' '}
                        Filter
                    </SimpleButton>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'right', mb: 1 }}>
                <Box sx={{ minWidth: 240, maxWidth: 320 }}>
                    <ErrorMessage error={error} style={{ marginTop: 4 }} />
                </Box>
            </Box>
        </Box>
    );
};

export default Filters;
